import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';
import { Col, Form, Row, Tab, Tabs, Spinner } from 'react-bootstrap';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import APIIntegrationDropDown from 'components/manage_vendors/vendor_details/APIIntegration/APIIntegrationDropDown';
import AttributePropagationFlagDropdown from 'components/manage_vendors/vendor_details/AttributePropagationFlagDropdown';
import { backgroundGrey4 } from 'lib/css/colors';
import ImageCategorySetting from 'components/manage_vendors/vendor_details/ImageCategorySetting';
import ImageFlagDropdown from 'components/manage_vendors/vendor_details/ImageFlagDropdown';
import { SwalToast } from 'lib/utils/toast';
import {
  UPDATE_CONTRACT_GMV,
  CONTRACT_TOTAL_ANNUALIZED_GMV_BY_VENDOR,
} from 'lib/networking/endpoints';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';
import useViewGMV from 'lib/hooks/useViewGMV';
import VendorCatalogSettingsView from 'components/manage_vendors/vendor_details/catalog_settings/VendorCatalogSettingsView';
import VendorRow from 'components/manage_vendors/vendor_details/VendorRow';

export const VENDOR_FIELDS = {
  PRIMARY_SOURCE: 'primary_source',
};

const vendorRows = {
  name: 'Distributor Name',
  vendor_url: 'Distributor Website',
  excluded_p0_sync_attributes: 'Attributes excluded from P0 sync',
  excluded_cnd_sync_fields: 'Fields excluded from CND to FSA sync',
  excluded_propagation_attributes: 'Attributes excluded from attribute propagation',
  rejected_brand_names: 'Rejected Brand Names',
  chains: 'Chains',
  [VENDOR_FIELDS.PRIMARY_SOURCE]: 'Primary Source (Master Catalog)',
  vendor_category: 'Select Distributor Category',
  alliance: 'Alliance',
  third_party: 'Third Party',
  archived: 'Archived',
  p0_sync_enabled: 'P0 sync is enabled',
  remove_product_on_cnd_to_fsa_sync: 'Enable removing products on Cnd to FSA sync',
  is_proprietary_images: 'Is proprietary images',
  cnd_image_sync_enabled: 'Cut and Dry image sync enabled',
  is_dp_partner: 'Is a distributor portal partner',
  is_ready_for_classification: 'Ready For Classification',
  is_a_trusted_source: 'Trusted source for product data',
  override_from_manufacturer_product: 'override data by manufacturer product',
  use_rosetta_categories: 'Use Rosetta Categories',
  populate_tag_family_data: 'Populate tag family data at P0 sync',
  is_test_vendor: 'Is Test Distributor',
  sync_unverified_brands: 'Sync Unverified Brands',
  index_attributes: 'Index Attributes',
  link_products_on_chain: 'Link products on the chains',
  bypass_image_deletion: 'By pass image deletion safety check in P0',
  influenced_enabled: 'Influenced enabled',
};

const VENDOR_ENRICH_TABS = {
  GENERAL_INFO: 'General Info',
  SETTINGS: 'Settings',
  CATALOG_SETTINGS: 'Catalog Settings',
};

function VendorDetailSplitView({ vendor, refetch, onVendorUpdated }) {
  const [activeTabKey, setActiveTabKey] = useState(VENDOR_ENRICH_TABS.GENERAL_INFO);
  const [vendorContractGmv, setVendorContractGmv] = useState(null);
  const [showGmv, setShowGmv] = useState(false);
  const viewGMV = useViewGMV();

  const { postData: updateContractGmv, loading: updateContractGmvLoading } = usePost(
    UPDATE_CONTRACT_GMV,
    () => SwalToast('success', `Successfully updated`),
    () => SwalToast('error', `Error Occurred`),
  );
  const { data: contractTotalGmvByVendor = {}, loading } = useGet(
    CONTRACT_TOTAL_ANNUALIZED_GMV_BY_VENDOR,
    {
      global_vendor_id: vendor.id,
    },
  );

  useEffect(() => {
    if (contractTotalGmvByVendor?.data) {
      setVendorContractGmv(contractTotalGmvByVendor?.data);
      setShowGmv(false);
    } else {
      setShowGmv(true);
    }
  }, [contractTotalGmvByVendor]);

  const gmvLoading = loading || updateContractGmvLoading;

  return (
    <Tabs activeKey={activeTabKey} onSelect={selectedKey => setActiveTabKey(selectedKey)}>
      <Tab eventKey={VENDOR_ENRICH_TABS.GENERAL_INFO} title={VENDOR_ENRICH_TABS.GENERAL_INFO}>
        <div className="px-5 mt-3">
          {Object.keys(vendorRows)
            .filter(key => key !== VENDOR_FIELDS.PRIMARY_SOURCE || vendor.is_mdm_vendor)
            .map(key => (
              <div key={key}>
                <VendorRow
                  label={key}
                  title={vendorRows[key]}
                  vendor={vendor}
                  refetch={refetch}
                  onVendorUpdated={onVendorUpdated}
                />
                <hr className={css(styles.separator)} />
              </div>
            ))}
        </div>
      </Tab>
      <Tab eventKey={VENDOR_ENRICH_TABS.SETTINGS} title={VENDOR_ENRICH_TABS.SETTINGS}>
        <div className="px-3 mt-3">
          <APIIntegrationDropDown globalVendorId={vendor.id} />
        </div>
        <div className="px-3 mt-3">
          <ImageFlagDropdown imageFlag={vendor.image_flag} />
        </div>
        <div className="px-3 mt-3">
          <AttributePropagationFlagDropdown
            attributePropagationFlag={vendor.attribute_propagation_flag}
          />
        </div>
        <div className="px-3 mt-3">
          <ImageCategorySetting imageCategory={vendor.image_category} />
        </div>
        {viewGMV && (
          <Row className="ml-1 mt-2">
            <Col sm="4">
              <Form.Group>
                <Form.Label>Contract GMV</Form.Label>
                {gmvLoading ? (
                  <Spinner animation="border" role="status" size="sm" />
                ) : (
                  <div className="d-flex align-items-center">
                    <Form.Control
                      type="text"
                      value={
                        showGmv ? vendorContractGmv : vendorContractGmv ? '*************' : '-'
                      }
                      onChange={e => setVendorContractGmv(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          updateContractGmv({
                            fsa_id: vendor.id,
                            name: vendor.name,
                            gmv: vendorContractGmv,
                          });
                        }
                      }}
                    />
                    <FontAwesomeIcon
                      icon={showGmv && vendorContractGmv ? faEye : faEyeSlash}
                      className="ml-2 cursor-pointer"
                      onClick={() => setShowGmv(!showGmv)}
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>
        )}
      </Tab>
      <Tab
        eventKey={VENDOR_ENRICH_TABS.CATALOG_SETTINGS}
        title={VENDOR_ENRICH_TABS.CATALOG_SETTINGS}
      >
        <div className="px-3 mt-3">
          <VendorCatalogSettingsView globalVendorId={vendor.id} />
        </div>
      </Tab>
    </Tabs>
  );
}

const styles = StyleSheet.create({
  separator: {
    color: backgroundGrey4,
    backgroundColor: backgroundGrey4,
    margin: '6px 0',
  },
});

VendorDetailSplitView.propTypes = {
  vendor: PropTypes.object,
  refetch: PropTypes.func,
  onVendorUpdated: PropTypes.func,
};

export default VendorDetailSplitView;
